import { Component, Input, OnChanges, SimpleChanges } from '@angular/core'

import { Store, select } from '@ngrx/store'
import { RootStoreState } from '../../../../../root-store/root-index'
import * as fromConfigSelectors from '../../../../../root-store/config-store/config.selectors'
import { filter, map, mergeMap, take, tap } from 'rxjs/operators'
import { EntityData } from '../../../models/entity-data'
import { GroupConfig } from '../../../models/group'
import { EntityService } from '../../../services/entity.service'
import { Observable, of } from 'rxjs'

// TODO is there a way to put subscriptions as async in html?
// TODO special BE filter api to build only those fields that are needed according to GroupConfig
// TODO filtering is hardcoded to be done on column "name"

@Component({
  selector: 'jml-lib-entity-group-relation',
  templateUrl: './entity-group-relation.component.html',
  styleUrls: ['./entity-group-relation.component.scss']
})
export class EntityGroupRelationComponent implements OnChanges {
  @Input() holderId: number
  @Input() relation: string

  groupConfig: GroupConfig
  // Contains all entities that are and can be members of the holder entity
  // it completely makes sense to load and keep all group members because if this control is shown as attachment and we select another entity,
  // we dont need to reload the potential members.
  allPotentialMembers: any[]
  // Contains all member entities
  groupMembers: any[] = []
  // Contains all entites which are not members of the holder
  nonGroupMembers: any[] = []

  constructor(
    private entityService: EntityService,
    private store$: Store<RootStoreState.State>
  ) {}

  getGroupConfig$(): Observable<GroupConfig> {
    return !!this.groupConfig
      ? of(this.groupConfig)
      : this.store$.pipe(
          select(fromConfigSelectors.selectGroupConfigs),
          filter((configs) => !!configs),
          map((configs) => configs[this.relation]),
          tap((groupConfig) => {
            if (!groupConfig) {
              console.error('Could not load ' + this.relation + ' group configuration!')
            }
            this.groupConfig = groupConfig
          })
        )
  }

  loadAllPotentialMembers$() {
    return !!this.allPotentialMembers
      ? of(this.allPotentialMembers)
      : this.getGroupConfig$().pipe(
          mergeMap((groupConfig) =>
            this.entityService.filter(groupConfig.memberFields, groupConfig.member, 1, 2147483647, undefined, '', '')
          ),
          map((entityData) => entityData.data),
          tap((data) => (this.allPotentialMembers = data))
        )
  }

  getMemberIdsAndBuildArrays$(): Observable<void> {
    return this.entityService.getGroupMemberIds(this.relation, this.holderId).pipe(
      map((memberIds) => {
        this.buildArray(memberIds)
      })
    )
  }

  buildArray(memberIds: number[]) {
    this.nonGroupMembers = new Array(Math.max(0, this.allPotentialMembers.length - memberIds.length)) // Maximum is because of rare cases where members are not in allPotentialMembers
    const tempGroupMembers: Map<number, any> = new Map()
    let i = 0
    this.allPotentialMembers.forEach((item) => {
      if (memberIds.includes(item['id'])) {
        tempGroupMembers.set(item['id'], item)
      } else {
        this.nonGroupMembers[i++] = item
      }
    })
    this.groupMembers = []
    memberIds.forEach((memberId) => {
      if (tempGroupMembers.has(memberId)) {
        this.groupMembers.push(tempGroupMembers.get(memberId))
      }
    })
  }

  init() {
    if (this.relation && this.holderId) {
      this.loadAllPotentialMembers$()
        .pipe(mergeMap((allPotentialMembers) => this.getMemberIdsAndBuildArrays$()))
        .subscribe()
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.relation) {
      this.groupConfig = undefined
      this.allPotentialMembers = undefined
      this.init()
    } else {
      this.getMemberIdsAndBuildArrays$().subscribe()
    }
  }

  addMembers(items: any[]) {
    const memberIds: number[] = []
    const positions: number[] = []

    items.forEach((item) => {
      const idx = this.groupMembers.findIndex((member) => member === item)
      if (idx < 0) {
        console.error('Did not find added Member in groupMembers!')
      } else {
        memberIds.push(item['id'])
        positions.push(1 + idx)
      }
    })
    this.entityService.addMembers(this.relation, this.holderId, memberIds, positions).subscribe()
  }

  removeMembers(items: any[]) {
    this.entityService
      .removeMembers(
        this.relation,
        this.holderId,
        items.map((item) => item['id'])
      )
      .subscribe()
  }

  reorderMembers() {
    const memberIds: number[] = this.groupMembers.map((item) => item.id)
    this.entityService.reorderMembers(this.relation, this.holderId, memberIds).subscribe()
  }

  trackByFunction = (index, item: any) => {
    return item ? item['id'] : null
  }

  getRepr(item: any): string {
    let result = ''
    this.groupConfig.memberFields.forEach((field) => {
      if (field.visible && item[field.field]) {
        result += item[field.field] + ' '
      }
    })
    return result
  }
}
